import { useMemo, useState } from 'react';
import { Plus, Pencil, Trash2 } from 'lucide-react';
import {
  DataTable,
  EmptyState,
  StatusPill,
  Pagination,
  type ColumnDef,
} from '@vnetwork/ui/data';
import {
  Button,
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  DrawerDescription,
  DrawerBody,
  DrawerFooter,
  Input,
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from '@vnetwork/ui/primitives';
import { Field } from '@vnetwork/ui/forms';
import { ConfirmDialog, useToast } from '@vnetwork/ui/feedback';
import { Toolbar } from '@vnetwork/ui/layout';
import {
  useContractSubcontracts,
  useCreateSubcontract,
  useUpdateSubcontract,
  useDeleteSubcontract,
  type SubcontractRow,
  type ContractStatus,
} from '@/api/contracts';
import { formatDate, formatMoney } from '@/lib/format';
import { contractStatusTone, statusLabel, CONTRACT_STATUS_OPTIONS } from '@/pages/contracts/contract-format';
import { TabError, TabLoading } from '@/pages/customers/detail/tab-states';
import { paginationView } from '@/pages/customers/pagination-summary';
import { DateInput } from '@/components/date-input';

const PAGE_SIZE = 20;

/** A subcontract is overdue once its expiry date is in the past. */
function isExpired(expiry?: string): boolean {
  if (!expiry) return false;
  const d = new Date(expiry);
  return !isNaN(d.getTime()) && d < new Date();
}

/** Build a local `YYYY-MM-DD` string without UTC shift (Asia/Saigon safe). */
function toLocalDate(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

interface SubcontractFormState {
  subcontract_code: string;
  subcontract_name: string;
  subcontract_note: string;
  subcontract_status: ContractStatus;
  subcontract_effective_date: string;
  subcontract_expiry_date: string;
}

function blankForm(): SubcontractFormState {
  return {
    subcontract_code: '',
    subcontract_name: '',
    subcontract_note: '',
    subcontract_status: 'pending',
    subcontract_effective_date: toLocalDate(new Date()),
    subcontract_expiry_date: '',
  };
}

function fromRow(row: SubcontractRow): SubcontractFormState {
  return {
    subcontract_code: row.subcontract_code ?? '',
    subcontract_name: row.subcontract_name ?? '',
    subcontract_note: '',
    subcontract_status: (row.subcontract_status as ContractStatus) ?? 'pending',
    subcontract_effective_date: row.subcontract_effective_date
      ? row.subcontract_effective_date.slice(0, 10)
      : toLocalDate(new Date()),
    subcontract_expiry_date: row.subcontract_expiry_date
      ? row.subcontract_expiry_date.slice(0, 10)
      : '',
  };
}

/**
 * Subcontracts tab — child contracts of this contract in a server-paginated
 * DataTable with inline create / edit / delete actions. Mirrors the legacy
 * subcontract columns: code, name (red when expired), effective/expiry dates,
 * status pill, and value. Create/edit use a Drawer form; delete uses a
 * ConfirmDialog.
 */
export function SubcontractsTab({ id }: { id: number }) {
  const toast = useToast();
  const [page, setPage] = useState(1);
  const { data, isLoading, isError, refetch } = useContractSubcontracts(id, {
    page,
    page_size: PAGE_SIZE,
  });

  // Drawer state
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editRow, setEditRow] = useState<SubcontractRow | null>(null);
  const [form, setForm] = useState<SubcontractFormState>(blankForm);
  const [formErrors, setFormErrors] = useState<Partial<Record<keyof SubcontractFormState, string>>>({});

  // Delete state
  const [deleteTarget, setDeleteTarget] = useState<SubcontractRow | null>(null);

  const createMutation = useCreateSubcontract(id);
  const updateMutation = useUpdateSubcontract(id, editRow?.subcontract_id ?? 0);
  const deleteMutation = useDeleteSubcontract(id);

  const rows = data?.results ?? [];
  const total = data?.pagination?.total ?? rows.length;
  const pageCount = data?.pagination?.total_pages ?? 1;

  /* ── Drawer helpers ────────────────────────────────────────────────────── */

  const openCreate = () => {
    setEditRow(null);
    setForm(blankForm());
    setFormErrors({});
    setDrawerOpen(true);
  };

  const openEdit = (row: SubcontractRow) => {
    setEditRow(row);
    setForm(fromRow(row));
    setFormErrors({});
    setDrawerOpen(true);
  };

  const closeDrawer = () => {
    setDrawerOpen(false);
    setEditRow(null);
    setFormErrors({});
  };

  const setField = <K extends keyof SubcontractFormState>(key: K, value: SubcontractFormState[K]) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    setFormErrors((prev) => ({ ...prev, [key]: undefined }));
  };

  const validate = (): boolean => {
    const errs: Partial<Record<keyof SubcontractFormState, string>> = {};
    if (!form.subcontract_name.trim()) errs.subcontract_name = 'Name is required.';
    if (!form.subcontract_effective_date) errs.subcontract_effective_date = 'Effective date is required.';
    if (
      form.subcontract_expiry_date &&
      form.subcontract_expiry_date <= form.subcontract_effective_date
    ) {
      errs.subcontract_expiry_date = 'Expiry must be after effective date.';
    }
    setFormErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSave = () => {
    if (!validate()) return;

    const subcontract = {
      subcontract_code: form.subcontract_code || undefined,
      subcontract_name: form.subcontract_name,
      subcontract_note: form.subcontract_note || undefined,
      subcontract_status: form.subcontract_status,
      subcontract_effective_date: form.subcontract_effective_date,
      subcontract_expiry_date: form.subcontract_expiry_date || undefined,
    };

    if (editRow) {
      updateMutation.mutate(
        { subcontract },
        {
          onSuccess: () => {
            toast.success('Subcontract updated');
            closeDrawer();
          },
          onError: (err) => {
            const msg = err instanceof Error ? err.message : 'Failed to update subcontract';
            toast.error('Failed to update subcontract', { description: msg });
          },
        },
      );
    } else {
      createMutation.mutate(
        { subcontract },
        {
          onSuccess: () => {
            toast.success('Subcontract created');
            closeDrawer();
          },
          onError: (err) => {
            const msg = err instanceof Error ? err.message : 'Failed to create subcontract';
            toast.error('Failed to create subcontract', { description: msg });
          },
        },
      );
    }
  };

  const handleDelete = () => {
    if (!deleteTarget) return;
    deleteMutation.mutate(
      { subcontractId: deleteTarget.subcontract_id },
      {
        onSuccess: () => {
          toast.success('Subcontract deleted');
          setDeleteTarget(null);
        },
        onError: (err) => {
          const msg = err instanceof Error ? err.message : 'Failed to delete subcontract';
          toast.error('Failed to delete subcontract', { description: msg });
          setDeleteTarget(null);
        },
      },
    );
  };

  /* ── Columns ───────────────────────────────────────────────────────────── */

  const columns = useMemo<ColumnDef<SubcontractRow, unknown>[]>(
    () => [
      {
        id: 'subcontract_code',
        header: 'Code',
        cell: ({ row }) => (
          <span style={{ fontWeight: 'var(--vn-font-weight-medium)' }}>
            {row.original.subcontract_code || '—'}
          </span>
        ),
      },
      {
        id: 'subcontract_name',
        header: 'Name',
        cell: ({ row }) => {
          const expired = isExpired(row.original.subcontract_expiry_date);
          return (
            <span
              style={{
                color: expired ? 'var(--vn-danger-text)' : undefined,
                fontWeight: 'var(--vn-font-weight-medium)',
                overflowWrap: 'anywhere',
              }}
            >
              {row.original.subcontract_name || '—'}
            </span>
          );
        },
      },
      {
        id: 'subcontract_effective_date',
        header: 'Effective',
        cell: ({ row }) => formatDate(row.original.subcontract_effective_date),
      },
      {
        id: 'subcontract_expiry_date',
        header: 'Expiry',
        cell: ({ row }) => {
          const expired = isExpired(row.original.subcontract_expiry_date);
          return (
            <span style={{ color: expired ? 'var(--vn-danger-text)' : undefined }}>
              {formatDate(row.original.subcontract_expiry_date)}
            </span>
          );
        },
      },
      {
        id: 'subcontract_status',
        header: 'Status',
        cell: ({ row }) => (
          <StatusPill tone={contractStatusTone(row.original.subcontract_status)}>
            {statusLabel(row.original.subcontract_status)}
          </StatusPill>
        ),
      },
      {
        id: 'contract_value',
        header: () => <div style={{ textAlign: 'right' }}>Value</div>,
        cell: ({ row }) => (
          <div style={{ textAlign: 'right' }}>{formatMoney(row.original.contract_value)}</div>
        ),
      },
      {
        id: '_actions',
        header: '',
        cell: ({ row }) => (
          <div style={{ display: 'flex', gap: 'var(--vn-space-1)', justifyContent: 'flex-end' }}>
            <Button
              size="sm"
              variant="ghost"
              aria-label="Edit subcontract"
              onClick={() => openEdit(row.original)}
            >
              <Pencil size={13} />
            </Button>
            <Button
              size="sm"
              variant="danger"
              aria-label="Delete subcontract"
              onClick={() => setDeleteTarget(row.original)}
            >
              <Trash2 size={13} />
            </Button>
          </div>
        ),
      },
    ],
    [],
  );

  /* ── Render ────────────────────────────────────────────────────────────── */

  if (isError) return <TabError title="Failed to load subcontracts" onRetry={() => refetch()} />;
  if (isLoading) return <TabLoading />;

  const isSaving = createMutation.isPending || updateMutation.isPending;

  return (
    <>
      <Toolbar
        style={{ marginBottom: 0 }}
        end={
          <Button size="sm" variant="primary" startIcon={<Plus size={14} />} onClick={openCreate}>
            Add subcontract
          </Button>
        }
      />

      <DataTable
        columns={columns}
        data={rows}
        enableSorting={false}
        getRowId={(r) => String(r.subcontract_id)}
        emptyState={
          <EmptyState
            title="No subcontracts"
            description="This contract has no subcontracts yet."
          />
        }
        footer={(() => {
          const view = paginationView(page, PAGE_SIZE, rows.length, total, pageCount);
          return (
            <Pagination
              page={page}
              pageCount={view.pageCount}
              onPageChange={setPage}
              summary={view.summary}
            />
          );
        })()}
      />

      {/* Create / Edit Drawer */}
      <Drawer open={drawerOpen} onOpenChange={(o) => { if (!o) closeDrawer(); }}>
        <DrawerContent>
          <DrawerHeader>
            <DrawerTitle>{editRow ? 'Edit subcontract' : 'Add subcontract'}</DrawerTitle>
            <DrawerDescription>
              {editRow
                ? `Editing ${editRow.subcontract_code || `#${editRow.subcontract_id}`}`
                : 'Create a new child contract under this contract.'}
            </DrawerDescription>
          </DrawerHeader>

          <DrawerBody>
            <div style={{ display: 'grid', gap: 'var(--vn-space-3)' }}>
              <Field label="Code" error={formErrors.subcontract_code}>
                {(fp) => (
                  <Input
                    {...fp}
                    value={form.subcontract_code}
                    onChange={(e) => setField('subcontract_code', e.target.value)}
                    placeholder="e.g. SC-001"
                  />
                )}
              </Field>

              <Field label="Name" required error={formErrors.subcontract_name}>
                {(fp) => (
                  <Input
                    {...fp}
                    value={form.subcontract_name}
                    onChange={(e) => setField('subcontract_name', e.target.value)}
                    placeholder="Subcontract name"
                  />
                )}
              </Field>

              <Field label="Status" error={formErrors.subcontract_status}>
                {(fp) => (
                  <Select
                    value={form.subcontract_status}
                    onValueChange={(v) => setField('subcontract_status', v as ContractStatus)}
                  >
                    <SelectTrigger {...fp}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {CONTRACT_STATUS_OPTIONS.map((opt) => (
                        <SelectItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </Field>

              <Field label="Effective date" required error={formErrors.subcontract_effective_date}>
                {(fp) => (
                  <DateInput
                    {...fp}
                    value={form.subcontract_effective_date}
                    onChange={(e) => setField('subcontract_effective_date', e.target.value)}
                  />
                )}
              </Field>

              <Field label="Expiry date" error={formErrors.subcontract_expiry_date}>
                {(fp) => (
                  <DateInput
                    {...fp}
                    value={form.subcontract_expiry_date}
                    onChange={(e) => setField('subcontract_expiry_date', e.target.value)}
                  />
                )}
              </Field>

              <Field label="Note" error={formErrors.subcontract_note}>
                {(fp) => (
                  <Input
                    {...fp}
                    value={form.subcontract_note}
                    onChange={(e) => setField('subcontract_note', e.target.value)}
                    placeholder="Optional note"
                  />
                )}
              </Field>
            </div>
          </DrawerBody>

          <DrawerFooter>
            <Button variant="secondary" onClick={closeDrawer} disabled={isSaving}>
              Cancel
            </Button>
            <Button variant="primary" onClick={handleSave} loading={isSaving}>
              {editRow ? 'Save changes' : 'Create'}
            </Button>
          </DrawerFooter>
        </DrawerContent>
      </Drawer>

      {/* Delete confirm */}
      <ConfirmDialog
        open={!!deleteTarget}
        onOpenChange={(o) => { if (!o) setDeleteTarget(null); }}
        title="Delete subcontract"
        description={
          deleteTarget
            ? `Permanently delete subcontract "${deleteTarget.subcontract_name || deleteTarget.subcontract_code || `#${deleteTarget.subcontract_id}`}"?`
            : ''
        }
        confirmLabel="Delete"
        destructive
        loading={deleteMutation.isPending}
        onConfirm={handleDelete}
      />
    </>
  );
}
